import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import type { Quapp } from '../../../types/quapp'
import { usePWAInstall } from '../../../hooks/usePWAInstall'

type Phase = 'idle' | 'installing' | 'installed'

/**
 * Installer screen shown when a Quapp URL is opened in a browser (i.e. NOT yet
 * installed). The actual Quapp is never rendered here — it only runs once the
 * user installs it and launches it from their home screen (standalone mode).
 *
 *  - Android / desktop Chrome: one-tap Install (native prompt) -> Installing -> Installed
 *  - iOS: guided Share -> Add to Home Screen steps
 *  - Desktop (no prompt): a QR code to open on the phone
 */
export function InstallLanding({ quapp }: { quapp: Quapp }) {
  const { platform, installMethod, promptInstall } = usePWAInstall()
  const [phase, setPhase] = useState<Phase>('idle')

  useEffect(() => {
    const onInstalled = () => setPhase('installed')
    window.addEventListener('appinstalled', onInstalled)
    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [])

  const handleInstall = async () => {
    setPhase('installing')
    const ok = await promptInstall()
    setPhase(ok ? 'installed' : 'idle')
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-white">
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        {/* App icon */}
        {quapp.icon_url ? (
          <img
            src={quapp.icon_url}
            alt=""
            className="h-28 w-28 rounded-[26px] shadow-lg ring-1 ring-black/5"
          />
        ) : (
          <div className="flex h-28 w-28 items-center justify-center rounded-[26px] bg-orange-500 text-4xl font-bold text-white shadow-lg">
            {quapp.display_name.charAt(0)}
          </div>
        )}

        <h1 className="mt-6 text-2xl font-bold text-slate-900">{quapp.display_name}</h1>
        {(quapp.short_description || quapp.description) && (
          <p className="mt-2 max-w-xs text-sm text-slate-500">
            {quapp.short_description || quapp.description}
          </p>
        )}

        {/* CTA */}
        <div className="mt-8 w-full max-w-xs">
          {phase === 'installed' ? (
            <Installed name={quapp.display_name} />
          ) : phase === 'installing' ? (
            <Installing name={quapp.display_name} />
          ) : installMethod === 'prompt' ? (
            <>
              <button
                onClick={handleInstall}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-500 py-4 text-base font-semibold text-white shadow-sm transition-colors hover:bg-orange-600"
              >
                <DownloadIcon /> Install {quapp.display_name}
              </button>
              <p className="mt-3 text-xs text-slate-400">
                Adds it to your home screen — no app store needed.
              </p>
            </>
          ) : platform === 'ios' ? (
            <IosSteps />
          ) : platform === 'desktop' ? (
            <DesktopQr />
          ) : (
            <ManualSteps />
          )}
        </div>
      </div>

      {/* Store branding — a touch more present here; build marker subtle, below it */}
      <div className="flex flex-col items-center gap-1.5 pb-8">
        <div className="flex items-center gap-1.5 opacity-60">
          <span className="text-xs tracking-wide text-slate-500">powered by</span>
          <img src="/quapp.png" alt="Quapp" className="h-4 grayscale" />
        </div>
        <span className="text-[9px] text-slate-300">{__BUILD_ID__}</span>
      </div>
    </div>
  )
}

function Installing({ name }: { name: string }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-50 px-4 py-6">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-200 border-t-orange-500" />
      <p className="text-sm font-medium text-slate-700">Installing {name}…</p>
      <p className="text-xs text-slate-400">This will only take a moment.</p>
    </div>
  )
}

function Installed({ name }: { name: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl bg-green-50 px-4 py-6">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-500 text-white">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-800">{name} installed!</p>
      <p className="text-xs text-slate-500">Open it from your home screen.</p>
    </div>
  )
}

function IosSteps() {
  return (
    <div className="text-left">
      <p className="mb-3 text-center text-sm text-slate-600">Add to your home screen:</p>
      <ol className="space-y-2.5">
        <Step n={1}>
          <span>Tap the <strong className="text-slate-900">Share</strong> button</span>
          <ShareIcon />
        </Step>
        <Step n={2}>
          <span>Choose <strong className="text-slate-900">Add to Home Screen</strong></span>
          <PlusSquareIcon className="text-slate-700" />
        </Step>
        <Step n={3}>
          <span>Tap <strong className="text-slate-900">Add</strong></span>
        </Step>
      </ol>
    </div>
  )
}

function ManualSteps() {
  return (
    <div className="text-left">
      <p className="mb-3 text-center text-sm text-slate-600">Install from your browser menu:</p>
      <ol className="space-y-2.5">
        <Step n={1}>
          <span>Open the browser <strong className="text-slate-900">menu</strong> (⋮)</span>
        </Step>
        <Step n={2}>
          <span>Tap <strong className="text-slate-900">Add to Home screen</strong> / Install</span>
        </Step>
      </ol>
    </div>
  )
}

function DesktopQr() {
  const url = typeof window !== 'undefined' ? window.location.href : ''
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl bg-slate-50 p-5">
      <div className="rounded-xl bg-white p-3 shadow-sm">
        <QRCodeSVG value={url} size={140} />
      </div>
      <p className="text-sm font-medium text-slate-700">Open on your phone to install</p>
      <p className="text-xs text-slate-400">Scan this code with your camera.</p>
    </div>
  )
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm font-semibold text-orange-600">
        {n}
      </span>
      <span className="flex flex-1 items-center justify-between gap-2 text-sm text-slate-600">
        {children}
      </span>
    </li>
  )
}

function DownloadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 3v13M12 3l-4 4M12 3l4 4" />
      <path d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  )
}

function PlusSquareIcon({ className = '' }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`}>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  )
}
